import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { ProfileCompleteness } from '@/components/ProfileCompleteness';
import { ProfileHealth } from '@/components/ProfileHealth';
import BillingModal from '@/components/BillingModal';
import {
  getCompanyProfile,
  createCompanyProfile,
  getApplications,
  createApplication,
  analyzeApplication,
  submitApplication,
  getNotifications,
  getUnreadCount,
  getCountries,
  getSectors,
  uploadDocument,
  getDocuments,
} from '@/lib/api';
import {
  Building2,
  FileText,
  Bell,
  LogOut,
  Plus,
  Send,
  Brain,
  CheckCircle,
  Clock,
  AlertTriangle,
  DollarSign,
  TrendingUp,
  FileUp,
  Menu,
  X,
  Home,
  User,
  Briefcase,
  RefreshCw,
  Eye,
  CreditCard,
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_creditai-africa/artifacts/q9fyvy5m_ChatGPT%20Image%20Feb%201%2C%202026%2C%2012_34_17%20PM.png";

const NAV_ITEMS = [
  { id: 'dashboard', icon: Building2, label: 'Dashboard' },
  { id: 'applications', icon: Briefcase, label: 'Applications' },
  { id: 'documents', icon: FileText, label: 'Documents' },
  { id: 'profile', icon: User, label: 'Profile' },
  { id: 'billing', icon: CreditCard, label: 'Billing' },
];

const STATUS_CONFIG = {
  DRAFT: { color: 'bg-slate-100 text-slate-700', icon: Clock },
  SUBMITTED: { color: 'bg-blue-100 text-blue-700', icon: Send },
  UNDER_REVIEW: { color: 'bg-amber-100 text-amber-700', icon: Eye },
  MATCHED: { color: 'bg-purple-100 text-purple-700', icon: CheckCircle },
  APPROVED: { color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  DECLINED: { color: 'bg-red-100 text-red-700', icon: AlertTriangle },
  FUNDED: { color: 'bg-emerald-100 text-emerald-700', icon: DollarSign },
};

function formatCurrency(amount, currency = 'USD') {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export default function CompanyDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [applications, setApplications] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [countries, setCountries] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [showNewAppForm, setShowNewAppForm] = useState(false);
  const [showProfileForm, setShowProfileForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showBillingModal, setShowBillingModal] = useState(false);

  const [profileForm, setProfileForm] = useState({
    company_name: '',
    country: '',
    sector: '',
    registration_number: '',
    year_established: '',
    employee_count: '',
    annual_revenue: '',
    website: '',
    description: '',
    contact_phone: '',
    contact_address: '',
  });

  const [appForm, setAppForm] = useState({
    loan_amount: '',
    currency: 'USD',
    tenor_months: '12',
    purpose: '',
    use_of_funds: '',
    repayment_source: '',
    collateral_offered: '',
    collateral_value: '',
    urgency: 'NORMAL',
    annual_revenue: '',
    net_profit: '',
    existing_debt: '',
    years_in_business: '',
    export_percentage: '',
    fx_exposure: '',
  });

  const [appDocuments, setAppDocuments] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [countriesData, sectorsData] = await Promise.all([
        getCountries(),
        getSectors(),
      ]);
      setCountries(countriesData);
      setSectors(sectorsData);

      try {
        const profileData = await getCompanyProfile();
        setProfile(profileData);
      } catch (e) {
        if (e.response?.status === 404) {
          // Redirect to onboarding wizard
          navigate('/company/onboarding');
          return;
        }
      }

      const [appsData, notifsData, unreadData] = await Promise.all([
        getApplications(),
        getNotifications(),
        getUnreadCount(),
      ]);
      setApplications(appsData);
      setNotifications(notifsData);
      setUnreadCount(unreadData.unread_count);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProfile = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const data = { ...profileForm };
      if (data.year_established) data.year_established = parseInt(data.year_established);
      if (data.employee_count) data.employee_count = parseInt(data.employee_count);
      if (data.annual_revenue) data.annual_revenue = parseFloat(data.annual_revenue);
      
      const newProfile = await createCompanyProfile(data);
      setProfile(newProfile);
      setShowProfileForm(false);
      toast.success('Company profile created!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create profile');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateApplication = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const data = {
        loan_amount: parseFloat(appForm.loan_amount),
        currency: appForm.currency,
        tenor_months: parseInt(appForm.tenor_months),
        purpose: appForm.purpose,
        use_of_funds: appForm.use_of_funds || null,
        repayment_source: appForm.repayment_source || null,
        collateral_offered: appForm.collateral_offered || null,
        collateral_value: appForm.collateral_value ? parseFloat(appForm.collateral_value) : null,
        urgency: appForm.urgency,
        annual_revenue: appForm.annual_revenue ? parseFloat(appForm.annual_revenue) : null,
        net_profit: appForm.net_profit ? parseFloat(appForm.net_profit) : null,
        existing_debt: appForm.existing_debt ? parseFloat(appForm.existing_debt) : null,
        years_in_business: appForm.years_in_business ? parseInt(appForm.years_in_business) : null,
        export_percentage: appForm.export_percentage ? parseFloat(appForm.export_percentage) : null,
        fx_exposure: appForm.fx_exposure ? parseFloat(appForm.fx_exposure) : null,
      };
      
      const newApp = await createApplication(data);
      setApplications([newApp, ...applications]);
      setShowNewAppForm(false);
      setAppForm({
        loan_amount: '',
        currency: 'USD',
        tenor_months: '12',
        purpose: '',
        use_of_funds: '',
        repayment_source: '',
        collateral_offered: '',
        collateral_value: '',
        urgency: 'NORMAL',
        annual_revenue: '',
        net_profit: '',
        existing_debt: '',
        years_in_business: '',
        export_percentage: '',
        fx_exposure: '',
      });
      toast.success('Application created!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create application');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAnalyze = async (appId) => {
    try {
      toast.info('Running AI analysis...');
      const result = await analyzeApplication(appId);
      await fetchData();
      toast.success(`Analysis complete! Risk Grade: ${result.risk_grade}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Analysis failed');
    }
  };

  const handleSubmit = async (appId) => {
    try {
      toast.info('Submitting application...');
      const result = await submitApplication(appId);
      await fetchData();
      toast.success(result.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Submission failed');
    }
  };

  const totalRequested = applications.reduce((sum, app) => sum + app.loan_amount, 0);
  const submittedApps = applications.filter(a => a.status !== 'DRAFT').length;
  const matchedApps = applications.filter(a => a.interested_funders?.length > 0).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-500">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Show profile form if no profile exists
  if (showProfileForm || !profile) {
    return (
      <div className="min-h-screen bg-slate-50 p-4 md:p-8">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-center mb-8">
            <img src={LOGO_URL} alt="Apaka" className="h-12" />
          </div>
          
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="font-heading text-2xl">Create Your Company Profile</CardTitle>
              <CardDescription>Tell us about your company to get started with funding applications</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateProfile} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Company Name *</Label>
                    <Input
                      value={profileForm.company_name}
                      onChange={(e) => setProfileForm({ ...profileForm, company_name: e.target.value })}
                      required
                      placeholder="Acme Corp"
                      data-testid="profile-company-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Registration Number</Label>
                    <Input
                      value={profileForm.registration_number}
                      onChange={(e) => setProfileForm({ ...profileForm, registration_number: e.target.value })}
                      placeholder="RC123456"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Country *</Label>
                    <Select
                      value={profileForm.country}
                      onValueChange={(v) => setProfileForm({ ...profileForm, country: v })}
                      required
                    >
                      <SelectTrigger data-testid="profile-country">
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                      <SelectContent>
                        {countries.map((c) => (
                          <SelectItem key={c.code} value={c.name}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Sector *</Label>
                    <Select
                      value={profileForm.sector}
                      onValueChange={(v) => setProfileForm({ ...profileForm, sector: v })}
                      required
                    >
                      <SelectTrigger data-testid="profile-sector">
                        <SelectValue placeholder="Select sector" />
                      </SelectTrigger>
                      <SelectContent>
                        {sectors.map((s) => (
                          <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Year Established</Label>
                    <Input
                      type="number"
                      value={profileForm.year_established}
                      onChange={(e) => setProfileForm({ ...profileForm, year_established: e.target.value })}
                      placeholder="2015"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Employee Count</Label>
                    <Input
                      type="number"
                      value={profileForm.employee_count}
                      onChange={(e) => setProfileForm({ ...profileForm, employee_count: e.target.value })}
                      placeholder="50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Annual Revenue (USD)</Label>
                    <Input
                      type="number"
                      value={profileForm.annual_revenue}
                      onChange={(e) => setProfileForm({ ...profileForm, annual_revenue: e.target.value })}
                      placeholder="1000000"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Company Description</Label>
                  <Textarea
                    value={profileForm.description}
                    onChange={(e) => setProfileForm({ ...profileForm, description: e.target.value })}
                    placeholder="Brief description of your company and what you do..."
                    rows={3}
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Website</Label>
                    <Input
                      value={profileForm.website}
                      onChange={(e) => setProfileForm({ ...profileForm, website: e.target.value })}
                      placeholder="https://example.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Contact Phone</Label>
                    <Input
                      value={profileForm.contact_phone}
                      onChange={(e) => setProfileForm({ ...profileForm, contact_phone: e.target.value })}
                      placeholder="+234 800 000 0000"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Business Address</Label>
                  <Textarea
                    value={profileForm.contact_address}
                    onChange={(e) => setProfileForm({ ...profileForm, contact_address: e.target.value })}
                    placeholder="Full business address..."
                    rows={2}
                  />
                </div>

                <Button type="submit" disabled={submitting} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="create-profile-btn">
                  {submitting ? 'Creating...' : 'Create Profile'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-slate-900 border-b border-slate-800 h-16 flex items-center justify-between px-4">
        <img src={LOGO_URL} alt="Apaka" className="h-10 brightness-0 invert" />
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 text-white">
          {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 bottom-0 w-64 bg-slate-900 text-white z-40 transform transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}>
        <div className="p-4 border-b border-slate-800">
          <img src={LOGO_URL} alt="Apaka" className="h-12 brightness-0 invert" />
        </div>
        
        <div className="p-4 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-blue-600 flex items-center justify-center">
              <Building2 className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{profile?.company_name}</div>
              <div className="text-xs text-slate-400">Company Account</div>
            </div>
          </div>
        </div>

        <nav className="p-4 space-y-1">
          {NAV_ITEMS.map((item) => (
            item.link ? (
              <Link
                key={item.id}
                to={item.link}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-slate-400 hover:text-white hover:bg-slate-800"
                data-testid={`nav-${item.id}`}
              >
                <item.icon className="h-5 w-5" />
                <span>{item.label}</span>
              </Link>
            ) : (
              <button
                key={item.id}
                onClick={() => { 
                  if (item.id === 'billing') {
                    setShowBillingModal(true);
                  } else {
                    setActiveTab(item.id); 
                  }
                  setSidebarOpen(false); 
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  activeTab === item.id
                    ? 'bg-blue-600 text-white'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                }`}
                data-testid={`nav-${item.id}`}
              >
                <item.icon className="h-5 w-5" />
                <span>{item.label}</span>
              </button>
            )
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-800">
          <button
            onClick={logout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
            data-testid="logout-btn"
          >
            <LogOut className="h-5 w-5" />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/50 z-30" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-heading text-2xl font-bold text-slate-900" data-testid="page-title">
                {activeTab === 'dashboard' && 'Company Dashboard'}
                {activeTab === 'applications' && 'Funding Applications'}
                {activeTab === 'documents' && 'Documents'}
                {activeTab === 'profile' && 'Company Profile'}
              </h1>
              <p className="text-slate-500">Welcome back, {user?.full_name}</p>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" size="icon" className="relative" data-testid="notifications-btn">
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </Button>
              <Button onClick={fetchData} variant="outline" size="icon">
                <RefreshCw className="h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && (
            <div className="space-y-6">
              {/* Profile Completeness & Health */}
              <div className="grid md:grid-cols-2 gap-4">
                <ProfileCompleteness profile={profile} type="company" />
                <ProfileHealth userRole="COMPANY" />
              </div>
              
              {/* Stats */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="border-slate-200" data-testid="stat-total-requested">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 text-slate-500 mb-2">
                      <DollarSign className="h-4 w-4" />
                      <span className="text-sm">Total Requested</span>
                    </div>
                    <div className="text-2xl font-bold">{formatCurrency(totalRequested)}</div>
                  </CardContent>
                </Card>
                <Card className="border-slate-200" data-testid="stat-applications">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 text-slate-500 mb-2">
                      <FileText className="h-4 w-4" />
                      <span className="text-sm">Applications</span>
                    </div>
                    <div className="text-2xl font-bold">{applications.length}</div>
                  </CardContent>
                </Card>
                <Card className="border-slate-200" data-testid="stat-submitted">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 text-slate-500 mb-2">
                      <Send className="h-4 w-4" />
                      <span className="text-sm">Submitted</span>
                    </div>
                    <div className="text-2xl font-bold">{submittedApps}</div>
                  </CardContent>
                </Card>
                <Card className="border-slate-200" data-testid="stat-interested">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 text-slate-500 mb-2">
                      <TrendingUp className="h-4 w-4" />
                      <span className="text-sm">Funder Interest</span>
                    </div>
                    <div className="text-2xl font-bold">{matchedApps}</div>
                  </CardContent>
                </Card>
              </div>

              {/* Quick Actions */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="font-heading">Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="flex flex-wrap gap-4">
                  <Button onClick={() => { setActiveTab('applications'); setShowNewAppForm(true); }} className="bg-blue-600 hover:bg-blue-700" data-testid="new-application-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    New Application
                  </Button>
                  <Button variant="outline" onClick={() => setActiveTab('documents')}>
                    <FileUp className="h-4 w-4 mr-2" />
                    Upload Documents
                  </Button>
                </CardContent>
              </Card>

              {/* Recent Applications */}
              <Card className="border-slate-200">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="font-heading">Recent Applications</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setActiveTab('applications')}>
                    View All
                  </Button>
                </CardHeader>
                <CardContent>
                  {applications.length === 0 ? (
                    <div className="text-center py-8 text-slate-400">
                      <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No applications yet</p>
                      <Button onClick={() => { setActiveTab('applications'); setShowNewAppForm(true); }} className="mt-4 bg-blue-600 hover:bg-blue-700">
                        Create Your First Application
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {applications.slice(0, 3).map((app) => {
                        const StatusIcon = STATUS_CONFIG[app.status]?.icon || Clock;
                        return (
                          <div key={app.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                            <div>
                              <div className="font-medium">{formatCurrency(app.loan_amount, app.currency)}</div>
                              <div className="text-sm text-slate-500">{app.purpose?.slice(0, 50)}...</div>
                            </div>
                            <div className="flex items-center gap-3">
                              {app.risk_grade && (
                                <Badge variant="outline">{app.risk_grade}</Badge>
                              )}
                              <Badge className={STATUS_CONFIG[app.status]?.color}>
                                <StatusIcon className="h-3 w-3 mr-1" />
                                {app.status}
                              </Badge>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Notifications */}
              {notifications.length > 0 && (
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="font-heading">Recent Notifications</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {notifications.slice(0, 5).map((notif) => (
                        <div key={notif.id} className={`p-4 rounded-lg ${notif.is_read ? 'bg-slate-50' : 'bg-blue-50 border border-blue-100'}`}>
                          <div className="font-medium">{notif.title}</div>
                          <div className="text-sm text-slate-500">{notif.message}</div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Applications Tab */}
          {activeTab === 'applications' && (
            <div className="space-y-6">
              <div className="flex justify-end">
                <Button onClick={() => setShowNewAppForm(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="create-app-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  New Application
                </Button>
              </div>

              {showNewAppForm && (
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle>New Funding Application</CardTitle>
                    <CardDescription>Submit your funding request to be matched with suitable funders</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleCreateApplication} className="space-y-6">
                      <div className="grid md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label>Loan Amount *</Label>
                          <Input
                            type="number"
                            value={appForm.loan_amount}
                            onChange={(e) => setAppForm({ ...appForm, loan_amount: e.target.value })}
                            required
                            placeholder="1000000"
                            data-testid="app-loan-amount"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Currency</Label>
                          <Select value={appForm.currency} onValueChange={(v) => setAppForm({ ...appForm, currency: v })}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="USD">USD</SelectItem>
                              <SelectItem value="EUR">EUR</SelectItem>
                              <SelectItem value="GBP">GBP</SelectItem>
                              <SelectItem value="NGN">NGN</SelectItem>
                              <SelectItem value="KES">KES</SelectItem>
                              <SelectItem value="ZAR">ZAR</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Tenor (months)</Label>
                          <Select value={appForm.tenor_months} onValueChange={(v) => setAppForm({ ...appForm, tenor_months: v })}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="6">6 months</SelectItem>
                              <SelectItem value="12">12 months</SelectItem>
                              <SelectItem value="24">24 months</SelectItem>
                              <SelectItem value="36">36 months</SelectItem>
                              <SelectItem value="48">48 months</SelectItem>
                              <SelectItem value="60">60 months</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Purpose of Loan *</Label>
                        <Textarea
                          value={appForm.purpose}
                          onChange={(e) => setAppForm({ ...appForm, purpose: e.target.value })}
                          required
                          placeholder="Describe the purpose of this funding request..."
                          rows={3}
                          data-testid="app-purpose"
                        />
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Use of Funds</Label>
                          <Input
                            value={appForm.use_of_funds}
                            onChange={(e) => setAppForm({ ...appForm, use_of_funds: e.target.value })}
                            placeholder="Working capital, equipment, expansion..."
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Repayment Source</Label>
                          <Input
                            value={appForm.repayment_source}
                            onChange={(e) => setAppForm({ ...appForm, repayment_source: e.target.value })}
                            placeholder="Operating cash flows, contract payments..."
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Collateral Offered</Label>
                          <Input
                            value={appForm.collateral_offered}
                            onChange={(e) => setAppForm({ ...appForm, collateral_offered: e.target.value })}
                            placeholder="Real estate, equipment, inventory..."
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Collateral Value (USD)</Label>
                          <Input
                            type="number"
                            value={appForm.collateral_value}
                            onChange={(e) => setAppForm({ ...appForm, collateral_value: e.target.value })}
                            placeholder="500000"
                          />
                        </div>
                      </div>

                      <div className="border-t pt-4">
                        <h4 className="font-medium mb-4">Financial Information (helps with AI analysis)</h4>
                        <div className="grid md:grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <Label>Annual Revenue (USD)</Label>
                            <Input
                              type="number"
                              value={appForm.annual_revenue}
                              onChange={(e) => setAppForm({ ...appForm, annual_revenue: e.target.value })}
                              placeholder="5000000"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Net Profit (USD)</Label>
                            <Input
                              type="number"
                              value={appForm.net_profit}
                              onChange={(e) => setAppForm({ ...appForm, net_profit: e.target.value })}
                              placeholder="500000"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Existing Debt (USD)</Label>
                            <Input
                              type="number"
                              value={appForm.existing_debt}
                              onChange={(e) => setAppForm({ ...appForm, existing_debt: e.target.value })}
                              placeholder="200000"
                            />
                          </div>
                        </div>
                        <div className="grid md:grid-cols-3 gap-4 mt-4">
                          <div className="space-y-2">
                            <Label>Years in Business</Label>
                            <Input
                              type="number"
                              value={appForm.years_in_business}
                              onChange={(e) => setAppForm({ ...appForm, years_in_business: e.target.value })}
                              placeholder="5"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Export % of Revenue</Label>
                            <Input
                              type="number"
                              value={appForm.export_percentage}
                              onChange={(e) => setAppForm({ ...appForm, export_percentage: e.target.value })}
                              placeholder="30"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>FX Exposure %</Label>
                            <Input
                              type="number"
                              value={appForm.fx_exposure}
                              onChange={(e) => setAppForm({ ...appForm, fx_exposure: e.target.value })}
                              placeholder="25"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Document Upload Section */}
                      <div className="border-t pt-4">
                        <h4 className="font-medium mb-4 flex items-center gap-2">
                          <FileUp className="h-4 w-4" />
                          Supporting Documents
                        </h4>
                        <p className="text-sm text-slate-500 mb-4">
                          Upload financial statements, contracts, and other relevant documents to strengthen your application.
                        </p>
                        
                        <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
                          <input 
                            type="file" 
                            id="app-doc-upload" 
                            className="hidden" 
                            multiple
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                            onChange={async (e) => {
                              const files = Array.from(e.target.files || []);
                              if (files.length === 0) return;
                              
                              setUploadingDoc(true);
                              const newDocs = [];
                              
                              for (const file of files) {
                                try {
                                  const result = await uploadDocument(file, profile?.id || 'temp', file.name.includes('financial') ? 'financial_statement' : 'other');
                                  newDocs.push({
                                    id: result.id,
                                    filename: result.filename,
                                    document_type: result.document_type,
                                    file_size: result.file_size,
                                  });
                                  toast.success(`Uploaded: ${file.name}`);
                                } catch (err) {
                                  toast.error(`Failed to upload: ${file.name}`);
                                }
                              }
                              
                              setAppDocuments(prev => [...prev, ...newDocs]);
                              setUploadingDoc(false);
                              e.target.value = '';
                            }}
                          />
                          <FileUp className="h-10 w-10 text-slate-400 mx-auto mb-2" />
                          <p className="text-sm text-slate-600 mb-2">
                            Drag and drop files here, or
                          </p>
                          <Button 
                            type="button"
                            variant="outline" 
                            onClick={() => document.getElementById('app-doc-upload')?.click()}
                            disabled={uploadingDoc}
                          >
                            {uploadingDoc ? 'Uploading...' : 'Choose Files'}
                          </Button>
                          <p className="text-xs text-slate-400 mt-2">
                            PDF, Word, Excel, Images (max 10MB each)
                          </p>
                        </div>

                        {appDocuments.length > 0 && (
                          <div className="mt-4 space-y-2">
                            <p className="text-sm font-medium text-slate-700">Uploaded Documents:</p>
                            {appDocuments.map((doc, idx) => (
                              <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                                <div className="flex items-center gap-3">
                                  <FileText className="h-5 w-5 text-blue-600" />
                                  <div>
                                    <p className="text-sm font-medium">{doc.filename}</p>
                                    <p className="text-xs text-slate-500">{doc.document_type}</p>
                                  </div>
                                </div>
                                <Button 
                                  type="button"
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => setAppDocuments(prev => prev.filter((_, i) => i !== idx))}
                                >
                                  Remove
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="flex gap-3 justify-end">
                        <Button type="button" variant="outline" onClick={() => { setShowNewAppForm(false); setAppDocuments([]); }}>
                          Cancel
                        </Button>
                        <Button type="submit" disabled={submitting} className="bg-blue-600 hover:bg-blue-700" data-testid="submit-app-btn">
                          {submitting ? 'Creating...' : 'Create Application'}
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              )}

              {/* Applications List */}
              <div className="space-y-4">
                {applications.map((app) => {
                  const StatusIcon = STATUS_CONFIG[app.status]?.icon || Clock;
                  return (
                    <Card key={app.id} className="border-slate-200">
                      <CardContent className="p-6">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <span className="text-xl font-bold">{formatCurrency(app.loan_amount, app.currency)}</span>
                              <Badge className={STATUS_CONFIG[app.status]?.color}>
                                <StatusIcon className="h-3 w-3 mr-1" />
                                {app.status}
                              </Badge>
                              {app.risk_grade && (
                                <Badge variant="outline">{app.risk_grade}</Badge>
                              )}
                            </div>
                            <p className="text-slate-600">{app.purpose}</p>
                            <div className="flex gap-4 mt-2 text-sm text-slate-500">
                              <span>{app.tenor_months} months</span>
                              {app.interested_funders?.length > 0 && (
                                <span className="text-emerald-600">{app.interested_funders.length} interested funders</span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            {app.status === 'DRAFT' && (
                              <>
                                <Button variant="outline" onClick={() => handleAnalyze(app.id)} data-testid={`analyze-${app.id}`}>
                                  <Brain className="h-4 w-4 mr-2" />
                                  AI Analysis
                                </Button>
                                <Button onClick={() => handleSubmit(app.id)} className="bg-blue-600 hover:bg-blue-700" data-testid={`submit-${app.id}`}>
                                  <Send className="h-4 w-4 mr-2" />
                                  Submit
                                </Button>
                              </>
                            )}
                            {app.status !== 'DRAFT' && (
                              <Button variant="outline">
                                <Eye className="h-4 w-4 mr-2" />
                                View Details
                              </Button>
                            )}
                          </div>
                        </div>

                        {app.initial_analysis && (
                          <div className="mt-4 pt-4 border-t">
                            <h4 className="font-medium mb-2">AI Analysis Summary</h4>
                            <div className="grid md:grid-cols-4 gap-4 text-sm">
                              <div>
                                <span className="text-slate-500">Risk Score:</span>
                                <span className="ml-2 font-medium">{app.initial_analysis.overall_score}/100</span>
                              </div>
                              <div>
                                <span className="text-slate-500">Default Prob:</span>
                                <span className="ml-2 font-medium">{(app.initial_analysis.default_probability * 100).toFixed(1)}%</span>
                              </div>
                              <div>
                                <span className="text-slate-500">Recommendation:</span>
                                <span className="ml-2 font-medium">{app.initial_analysis.recommendation}</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {applications.length === 0 && !showNewAppForm && (
                <Card className="border-slate-200">
                  <CardContent className="p-12 text-center">
                    <FileText className="h-16 w-16 mx-auto mb-4 text-slate-300" />
                    <h3 className="text-xl font-medium mb-2">No Applications Yet</h3>
                    <p className="text-slate-500 mb-4">Create your first funding application to get matched with funders</p>
                    <Button onClick={() => setShowNewAppForm(true)} className="bg-blue-600 hover:bg-blue-700">
                      <Plus className="h-4 w-4 mr-2" />
                      Create Application
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <div className="space-y-6">
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle>Upload Documents</CardTitle>
                  <CardDescription>Upload supporting documents for your funding applications</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center">
                    <FileUp className="h-12 w-12 mx-auto mb-4 text-slate-400" />
                    <p className="text-slate-600 mb-2">Drag and drop files here, or click to browse</p>
                    <p className="text-sm text-slate-400">Supported: PDF, DOC, XLS, Images (Max 10MB)</p>
                    <input type="file" className="hidden" id="file-upload" />
                    <Button variant="outline" className="mt-4" onClick={() => document.getElementById('file-upload')?.click()}>
                      Select Files
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Profile Tab */}
          {activeTab === 'profile' && profile && (
            <div className="space-y-6">
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle>Company Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label className="text-slate-500">Company Name</Label>
                      <p className="font-medium">{profile.company_name}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500">Registration Number</Label>
                      <p className="font-medium">{profile.registration_number || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500">Country</Label>
                      <p className="font-medium">{profile.country}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500">Sector</Label>
                      <p className="font-medium">{profile.sector}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500">Year Established</Label>
                      <p className="font-medium">{profile.year_established || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500">Employee Count</Label>
                      <p className="font-medium">{profile.employee_count || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500">Annual Revenue</Label>
                      <p className="font-medium">{profile.annual_revenue ? formatCurrency(profile.annual_revenue) : '-'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500">Website</Label>
                      <p className="font-medium">{profile.website || '-'}</p>
                    </div>
                  </div>
                  {profile.description && (
                    <div className="mt-6">
                      <Label className="text-slate-500">Description</Label>
                      <p className="mt-1">{profile.description}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </main>

      {/* Billing Modal */}
      <BillingModal open={showBillingModal} onClose={() => setShowBillingModal(false)} />
    </div>
  );
}
